/* eslint-disable dot-notation */
import Title from 'components/Title';
import React, { useCallback, useEffect, useState } from 'react';
import FilterText from 'components/FilterText';
import { useMainContext } from 'context/MainContext';
import {
  Button,
  Col,
  Form,
  Row,
  Table,
  Accordion,
  InputGroup,
  FormControl
} from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { enumServerApps } from 'enum/enumServerApps';
import { enumServerOS, enumServerOSLabel } from 'enum/enumServerOS';
import { enumServerBackup } from 'enum/enumServerBackup';
import { enumServerManagement } from 'enum/enumServerManagement';

import { RightArrow } from 'styled-icons/boxicons-solid';
import { Plus } from 'styled-icons/fa-solid';
import { Delete, Edit, Save } from 'styled-icons/material';

import { v4 as uuidv4 } from 'uuid';
import { masks } from 'utils/masks';

import { IQuote } from 'interfaces/IQuote';
import { IQuoteItem } from 'interfaces/IQuoteItem';

import { IClient } from 'interfaces/IClient';
import { ICommercialProduct } from 'interfaces/ICommercialProduct';
import { IUser } from 'interfaces/IUser';
import { IProductPrice } from 'interfaces/IProduct';
import { IQuoteServer } from 'interfaces/IQuoteServer';
import { ISalesChannel } from 'interfaces/ISalesChannel';

import { useUserService } from 'services/userService';
import { useQuoteService } from 'services/QuoteService';
import { useClientService } from 'services/clientService';
import { useProductPriceService } from 'services/productPriceService';
import { useSalesChannelService } from 'services/salesChannelService';
import { useCommercialProductService } from 'services/commercialProductService';
import {
  Container,
  AccordionHeaderContainer,
  AccordionHeaderLeft,
  AccordionHeaderRight
} from './styles';

const AddEditQuote: React.FC = () => {
  const navigate = useNavigate();
  const quoteId = useLocation().state as string;

  const { setDialog } = useMainContext();

  const { QuoteService } = useQuoteService();
  const { userService } = useUserService();
  const { clientService } = useClientService();
  const { salesChannelService } = useSalesChannelService();
  const { commercialProductService } = useCommercialProductService();

  const { productPriceService } = useProductPriceService();
  const [users, setUsers] = useState<IUser[] | null>(null);
  const [clients, setClients] = useState<IClient[] | null>(null);

  const [quoteServers, setQuoteServers] = useState<IQuoteServer[] | null>(null);
  const [salesChannels, setSalesChannels] = useState<ISalesChannel[] | null>(
    null
  );

  const [server, setServer] = useState<IQuoteServer>({
    Id: uuidv4(),
    Name: '',
    Vcpu: 0,
    Memory: 0,
    SO: 0,
    DiskSO: 50,
    DiskData: 0,
    DiskDbBackup: 0,
    Application: 0,
    Backup: 0,
    Management: 0,
    edr: false,
    Draas: false,
    DbBackup: false
  } as IQuoteServer);

  const [serverValidation, setServerValidation] = useState({
    Vcpu: false,
    Memory: false,
    DiskSO: false,
    SO: false
  });

  const [validated, setValidated] = useState(false);
  const [quoteAddEdit, setQuoteAddEdit] = useState<IQuote>({
    QuoteId: '',
    ClientId: 0,
    SalesChannelId: 0,
    VendorAdminUserId: '',
    ProjectName: '',
    PricelistType: 1,
    CreatedDate: new Date()
  } as IQuote);
  const [commercailProducts, setCommercailProducts] = useState<
    ICommercialProduct[] | null
  >(null);
  const [productPriceList, setProductPriceList] = useState<
    IProductPrice[] | null
  >(null);
  const [priceTableId, setPriceTableId] = useState<number>(1);

  const handlePriceTableChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newPriceTableId = Number(e.target.value);
    setPriceTableId(newPriceTableId);
    try {
      let ProductPrices: IProductPrice[] = [];
      if (newPriceTableId === 1) {
        //varejo
        const result = await productPriceService.findProductPriceByCompanyId(
          1, //golden
          1
        );
        ProductPrices = result.ProductPrices;
      } else if (newPriceTableId === 2) {
        //revenda
        const result = await productPriceService.findProductPriceByClientId(
          1, //golden
          1
        );
        ProductPrices = result.ProductPrices;
      }
      setProductPriceList(ProductPrices);
      // Atualiza todos os QuoteItems com os novos preços e define PricelistType na quote
      setQuoteAddEdit(prevState => ({
        ...prevState,
        PricelistType: newPriceTableId,
        QuoteItems: prevState.QuoteItems?.map(item => {
          const commercailProduct = commercailProducts?.find(
            p => p.CommercialProductId === item.CommercialProductId
          );
          const newPrice =
            ProductPrices.find(
              price => price.ProductId === commercailProduct?.ProductId
            )?.PriceCompany || 0;
          return { ...item, Price: newPrice, BasePrice: newPrice };
        })
      }));
    } catch (error) {
      console.error('Erro ao atualizar ProductPriceList', error);
    }
  };

  const handleConfirm = useCallback(async () => {
    try {
      if (quoteAddEdit?.QuoteId) await QuoteService.update(quoteAddEdit);
      else await QuoteService.add(quoteAddEdit);
      navigate(-1);
    } catch (error) {
      console.log(
        `error in AddEditUser - function handleConfirm, error:${error}`
      );
    }
  }, [quoteAddEdit, QuoteService, navigate]);

  const getPriceProduct = useCallback(
    (commercialProductId: number) => {
      const commercailProduct = commercailProducts?.find(
        cproduct => cproduct.CommercialProductId === commercialProductId
      );
      const productPrice = productPriceList?.find(
        price => price.ProductId === commercailProduct?.ProductId
      );
      return productPrice?.PriceCompany || 0;
    },
    [commercailProducts, productPriceList]
  );

  const calculateTotalByProductTypes = useCallback(
    (productTypes: number[]) => {
      if (!quoteAddEdit.QuoteItems || !commercailProducts) return 0;

      const total = quoteAddEdit.QuoteItems.reduce((acc, item) => {
        const product = commercailProducts.find(
          p =>
            p.CommercialProductId === item.CommercialProductId &&
            productTypes.includes(p.CommercialProductType)
        );

        if (product) {
          return acc + (item.Price || 0) * item.Quantity;
        }

        return acc;
      }, 0);

      if (productTypes.includes(0)) {
        return total - (quoteAddEdit.Discount || 0);
      }
      return total;
    },
    [quoteAddEdit.QuoteItems, commercailProducts, quoteAddEdit.Discount]
  );

  const getTotalServer = useCallback(
    (ServerID: string) => {
      const quoteItems = quoteAddEdit?.QuoteItems;
      if (!quoteItems) return 0;

      const quoteItem = quoteItems.find(item => {
        try {
          const details = JSON.parse(item.Details || '{}');
          return details.Id === ServerID;
        } catch (error) {
          console.error('Error parsing details:', error);
          return false;
        }
      });
      if (!quoteItem) return 0;

      const total = quoteItems.reduce((acc, item) => {
        if (item.QuoteItemUpperId === quoteItem.QuoteItemId) {
          return acc + (item.Price || 0) * item.Quantity;
        }
        return acc;
      }, (quoteItem.Price || 0) * quoteItem.Quantity);

      return total;
    },
    [quoteAddEdit]
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      const form = event.currentTarget;

      event.preventDefault();
      event.stopPropagation();
      setValidated(!form.checkValidity());

      if (form.checkValidity())
        setDialog({ show: true, callBackConfirm: handleConfirm });
    },
    [handleConfirm, setDialog]
  );

  const addServerProduct = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();

      // Validate required server fields
      const errors = {
        Vcpu: server.Vcpu <= 0,
        Memory: server.Memory <= 0,
        DiskSO: server.DiskSO < 50,
        SO: server.SO <= 0
      };
      if (errors.Vcpu || errors.Memory || errors.DiskSO || errors.SO) {
        setServerValidation(errors);
        return; // Do not add server if any field is invalid
      }

      // If valid, clear errors
      setServerValidation({
        Vcpu: false,
        Memory: false,
        DiskSO: false,
        SO: false
      });

      const serverJson = JSON.stringify(server);
      const newQuoteItems: IQuoteItem[] = [];

      // Item do Servidor
      const serverItem: IQuoteItem = {
        QuoteItemId: uuidv4(),
        QuoteId: quoteAddEdit.QuoteId,
        CommercialProductId: 1,
        Price: 0,
        BasePrice: 0,
        Quantity: 1,
        Details: serverJson
      };
      newQuoteItems.push(serverItem);

      // VCPU
      const vcpuItem: IQuoteItem = {
        QuoteItemId: uuidv4(),
        QuoteItemUpperId: serverItem.QuoteItemId,
        QuoteId: quoteAddEdit.QuoteId,
        CommercialProductId: 2,
        Price: getPriceProduct(2),
        BasePrice: getPriceProduct(2),
        Quantity: server.Vcpu
      };
      newQuoteItems.push(vcpuItem);

      // Memory
      const memoryItem: IQuoteItem = {
        QuoteItemId: uuidv4(),
        QuoteItemUpperId: serverItem.QuoteItemId,
        QuoteId: quoteAddEdit.QuoteId,
        CommercialProductId: 3,
        Price: getPriceProduct(3),
        BasePrice: getPriceProduct(3),
        Quantity: server.Memory
      };
      newQuoteItems.push(memoryItem);

      // Disks
      const diskSOItem: IQuoteItem = {
        QuoteItemId: uuidv4(),
        QuoteItemUpperId: serverItem.QuoteItemId,
        QuoteId: quoteAddEdit.QuoteId,
        CommercialProductId: 4,
        Price: getPriceProduct(4),
        BasePrice: getPriceProduct(4),
        Quantity: server.DiskSO + server.DiskData
      };
      newQuoteItems.push(diskSOItem);

      // Disk Data
      // const diskDataItem: IQuoteItem = {
      //   QuoteItemId: uuidv4(),
      //   QuoteItemUpperId: serverItem.QuoteItemId,
      //   QuoteId: quoteAddEdit.QuoteId,
      //   CommercialProductId: 4,
      //   Price: getPriceProduct(4),
      //   BasePrice: getPriceProduct(4),
      //   Quantity: server.DiskData
      // };
      // newQuoteItems.push(diskSOItem);

      // Application
      let commercialProductId = 0;
      let appquantity = 1;
      switch (server.Application) {
        case enumServerApps.SQLStandard:
          commercialProductId = 28;
          appquantity = server.Vcpu;
          break;
        case enumServerApps.SQLEnterprise:
          commercialProductId = 29;
          appquantity = server.Vcpu;
          break;
        default:
          break;
      }

      if (commercialProductId > 0) {
        newQuoteItems.push({
          QuoteItemId: uuidv4(),
          QuoteItemUpperId: serverItem.QuoteItemId,
          QuoteId: quoteAddEdit.QuoteId,
          CommercialProductId: commercialProductId,
          Price: getPriceProduct(commercialProductId),
          BasePrice: getPriceProduct(commercialProductId),
          Quantity: appquantity
        });
      }

      // S.O.
      commercialProductId = 0;
      let soQuantity = 1;
      switch (server.SO) {
        case enumServerOS.WindowsServer:
          commercialProductId = 22;
          break;
        case enumServerOS.SLESSAP:
          commercialProductId = 23;
          break;
        case enumServerOS.RedHatLinux:
          commercialProductId = 24;
          break;
        case enumServerOS.Suse:
          commercialProductId = 30;
          soQuantity = server.Vcpu;
          break;
        default:
          break;
      }
      if (commercialProductId > 0) {
        newQuoteItems.push({
          QuoteItemId: uuidv4(),
          QuoteItemUpperId: serverItem.QuoteItemId,
          QuoteId: quoteAddEdit.QuoteId,
          CommercialProductId: commercialProductId,
          Price: getPriceProduct(commercialProductId),
          BasePrice: getPriceProduct(commercialProductId),
          Quantity: soQuantity
        });
      }

      // Backup
      commercialProductId = 0;
      let commercialProductIdDisk = 0;
      let bkpquantity = 1;
      switch (server.Backup) {
        case enumServerBackup.Commvault:
          commercialProductId = 6;
          commercialProductIdDisk = 27;
          bkpquantity = server.DiskData + server.DiskDbBackup + server.DiskSO;
          break;
        case enumServerBackup.Guardian:
          commercialProductId = 7;
          commercialProductIdDisk = 9;
          bkpquantity = server.DiskData + server.DiskDbBackup + server.DiskSO;
          break;
        default:
          break;
      }
      if (commercialProductId > 0) {
        newQuoteItems.push({
          QuoteItemId: uuidv4(),
          QuoteItemUpperId: serverItem.QuoteItemId,
          QuoteId: quoteAddEdit.QuoteId,
          CommercialProductId: commercialProductId,
          Price: getPriceProduct(commercialProductId),
          BasePrice: getPriceProduct(commercialProductId),
          Quantity: 1
        });
        newQuoteItems.push({
          QuoteItemId: uuidv4(),
          QuoteItemUpperId: serverItem.QuoteItemId,
          QuoteId: quoteAddEdit.QuoteId,
          CommercialProductId: commercialProductIdDisk,
          Price: getPriceProduct(commercialProductIdDisk),
          BasePrice: getPriceProduct(commercialProductIdDisk),
          Quantity: bkpquantity
        });
      }

      // gerenciamento
      commercialProductId = 0;
      switch (server.Management) {
        case enumServerManagement.Standard:
          commercialProductId = 10;
          break;
        case enumServerManagement.Enterprise:
          commercialProductId = 11;
          break;
        case enumServerManagement.Database:
          commercialProductId = 12;
          break;
        case enumServerManagement.Basis:
          commercialProductId = 14;
          break;
        default:
          break;
      }
      if (commercialProductId > 0) {
        newQuoteItems.push({
          QuoteItemId: uuidv4(),
          QuoteItemUpperId: serverItem.QuoteItemId,
          QuoteId: quoteAddEdit.QuoteId,
          CommercialProductId: commercialProductId,
          Price: getPriceProduct(commercialProductId),
          BasePrice: getPriceProduct(commercialProductId),
          Quantity: 1
        });
      }

      // DRaaS
      if (server.Draas) {
        // agente
        newQuoteItems.push({
          QuoteItemId: uuidv4(),
          QuoteItemUpperId: serverItem.QuoteItemId,
          QuoteId: quoteAddEdit.QuoteId,
          CommercialProductId: 16,
          Price: getPriceProduct(16),
          BasePrice: getPriceProduct(16),
          Quantity: 1
        });
        // disco + jornaul
        newQuoteItems.push({
          QuoteItemId: uuidv4(),
          QuoteItemUpperId: serverItem.QuoteItemId,
          QuoteId: quoteAddEdit.QuoteId,
          CommercialProductId: 17,
          Price: getPriceProduct(17),
          BasePrice: getPriceProduct(17),
          Quantity: (server.DiskSO + server.DiskData) * 1.1 //  10%
        });
        // memoria
        newQuoteItems.push({
          QuoteItemId: uuidv4(),
          QuoteItemUpperId: serverItem.QuoteItemId,
          QuoteId: quoteAddEdit.QuoteId,
          CommercialProductId: 18,
          Price: getPriceProduct(18),
          BasePrice: getPriceProduct(18),
          Quantity: server.Memory * 0.3
        });
        // cpu
        newQuoteItems.push({
          QuoteItemId: uuidv4(),
          QuoteItemUpperId: serverItem.QuoteItemId,
          QuoteId: quoteAddEdit.QuoteId,
          CommercialProductId: 19,
          Price: getPriceProduct(19),
          BasePrice: getPriceProduct(19),
          Quantity: server.Vcpu * 0.3
        });
      }

      setQuoteAddEdit(prevState => {
        const QuoteItems = prevState.QuoteItems
          ? [...prevState.QuoteItems, ...newQuoteItems]
          : newQuoteItems;
        return { ...prevState, QuoteItems };
      });

      // salvando servidor para exibir na tabela
      setQuoteServers(prevState => {
        const newServer = { ...server };
        return [...(prevState || []), newServer];
      });

      setServer({
        Id: uuidv4(),
        Name: '',
        Vcpu: 0,
        Memory: 0,
        SO: 0,
        DiskSO: 0,
        DiskData: 0,
        DiskDbBackup: 0,
        Application: 0,
        Backup: 0,
        Management: 0,
        edr: false,
        Draas: false,
        DbBackup: false
      });
    },
    [server, quoteAddEdit, getPriceProduct]
  );

  const addNetworkProduct = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      console.log('addNetworkProduct called');

      // Recupera os valores do DOM fora da atualização do estado
      const networkProductValue = (
        document.querySelector(
          'select[name="networkProduct"]'
        ) as HTMLSelectElement
      )?.value;
      const networkProduct = parseInt(networkProductValue, 10);
      if (Number.isNaN(networkProduct)) {
        console.error('Valor de produto de rede inválido.');
        return;
      }

      const networkProductQtyValue = (
        document.querySelector(
          'input[name="networkProductQty"]'
        ) as HTMLInputElement
      )?.value;
      const networkProductQty = parseInt(networkProductQtyValue || '1', 10);
      if (Number.isNaN(networkProductQty)) {
        console.error('Valor de quantidade inválido.');
        return;
      }

      // Busca o produto selecionado usando o id
      const selectedProduct = commercailProducts?.find(
        product => product.CommercialProductId === networkProduct
      );
      if (!selectedProduct) {
        console.error('Produto não encontrado.');
        return;
      }

      // Busca o preço do produto
      const productPrice = productPriceList?.find(
        price => price.ProductId === selectedProduct.ProductId
      );
      if (!productPrice) {
        console.error('Preço do produto não encontrado.');
        return;
      }

      // Atualiza o estado com base nos valores recuperados e validados
      setQuoteAddEdit(prevState => {
        console.log('Updating state');

        // Cria uma cópia dos itens atuais (ou inicia um array vazio)
        const QuoteItems = prevState.QuoteItems
          ? [...prevState.QuoteItems]
          : [];

        // Verifica se o item já existe na lista
        const existingItemIndex = QuoteItems.findIndex(
          item => item.CommercialProductId === networkProduct
        );

        if (existingItemIndex >= 0) {
          // Atualiza a quantidade do item existente
          QuoteItems[existingItemIndex] = {
            ...QuoteItems[existingItemIndex],
            Quantity: QuoteItems[existingItemIndex].Quantity + networkProductQty
          };
        } else {
          // Adiciona um novo item à lista
          QuoteItems.push({
            QuoteItemId: uuidv4(),
            QuoteId: prevState.QuoteId,
            CommercialProductId: networkProduct,
            Price: productPrice.PriceCompany,
            BasePrice: productPrice.PriceCompany,
            Quantity: networkProductQty
          });
        }

        return { ...prevState, QuoteItems };
      });
    },
    [commercailProducts, productPriceList]
  );

  const addSecurityProduct = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      console.log('addNetworkProduct called');

      // Recupera os valores do DOM fora da atualização do estado
      const securityProductValue = (
        document.querySelector(
          'select[name="securityProduct"]'
        ) as HTMLSelectElement
      )?.value;
      const securityProduct = parseInt(securityProductValue, 10);
      if (Number.isNaN(securityProduct)) {
        console.error('Valor de produto de rede inválido.');
        return;
      }

      const securityProductQtyValue = (
        document.querySelector(
          'input[name="securityProductQty"]'
        ) as HTMLInputElement
      )?.value;
      const securityProductQty = parseInt(securityProductQtyValue || '1', 10);
      if (Number.isNaN(securityProductQty)) {
        console.error('Valor de quantidade inválido.');
        return;
      }

      // Busca o produto selecionado usando o id
      const selectedProduct = commercailProducts?.find(
        product => product.CommercialProductId === securityProduct
      );
      if (!selectedProduct) {
        console.error('Produto não encontrado.');
        return;
      }

      // Busca o preço do produto
      const productPrice = productPriceList?.find(
        price => price.ProductId === selectedProduct.ProductId
      );
      if (!productPrice) {
        alert('Preço do produto não encontrado.');
        return;
      }

      // Atualiza o estado com base nos valores recuperados e validados
      setQuoteAddEdit(prevState => {
        console.log('Updating state');

        // Cria uma cópia dos itens atuais (ou inicia um array vazio)
        const QuoteItems = prevState.QuoteItems
          ? [...prevState.QuoteItems]
          : [];

        // Verifica se o item já existe na lista
        const existingItemIndex = QuoteItems.findIndex(
          item => item.CommercialProductId === securityProduct
        );

        if (existingItemIndex >= 0) {
          // Atualiza a quantidade do item existente
          QuoteItems[existingItemIndex] = {
            ...QuoteItems[existingItemIndex],
            Quantity:
              QuoteItems[existingItemIndex].Quantity + securityProductQty
          };
        } else {
          // Adiciona um novo item à lista
          QuoteItems.push({
            QuoteItemId: uuidv4(),
            QuoteId: prevState.QuoteId,
            CommercialProductId: securityProduct,
            Price: productPrice.PriceCompany,
            BasePrice: productPrice.PriceCompany,
            Quantity: securityProductQty
          });
        }

        return { ...prevState, QuoteItems };
      });
    },
    [commercailProducts, productPriceList]
  );

  const addServiceProduct = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      console.log('addServiceProduct called');

      // Recupera os valores do DOM fora da atualização do estado
      const serviceProductValue = (
        document.querySelector(
          'select[name="serviceProduct"]'
        ) as HTMLSelectElement
      )?.value;
      const serviceProduct = parseInt(serviceProductValue, 10);
      if (Number.isNaN(serviceProduct)) {
        console.error('Valor de produto de rede inválido.');
        return;
      }

      const serviceProductQtyValue = (
        document.querySelector(
          'input[name="serviceProductQty"]'
        ) as HTMLInputElement
      )?.value;
      const serviceProductQty = parseInt(serviceProductQtyValue || '1', 10);
      if (Number.isNaN(serviceProductQty)) {
        console.error('Valor de quantidade inválido.');
        return;
      }

      // Busca o produto selecionado usando o id
      const selectedProduct = commercailProducts?.find(
        product => product.CommercialProductId === serviceProduct
      );
      if (!selectedProduct) {
        console.error('Produto não encontrado.');
        return;
      }

      // Busca o preço do produto
      const productPrice = productPriceList?.find(
        price => price.ProductId === selectedProduct.ProductId
      );
      if (!productPrice) {
        alert('Preço do produto não encontrado.');
        return;
      }

      // Atualiza o estado com base nos valores recuperados e validados
      setQuoteAddEdit(prevState => {
        console.log('Updating state');

        // Cria uma cópia dos itens atuais (ou inicia um array vazio)
        const QuoteItems = prevState.QuoteItems
          ? [...prevState.QuoteItems]
          : [];

        // Verifica se o item já existe na lista
        const existingItemIndex = QuoteItems.findIndex(
          item => item.CommercialProductId === serviceProduct
        );

        if (existingItemIndex >= 0) {
          // Atualiza a quantidade do item existente
          QuoteItems[existingItemIndex] = {
            ...QuoteItems[existingItemIndex],
            Quantity: QuoteItems[existingItemIndex].Quantity + serviceProductQty
          };
        } else {
          // Adiciona um novo item à lista
          QuoteItems.push({
            QuoteItemId: uuidv4(),
            QuoteId: prevState.QuoteId,
            CommercialProductId: serviceProduct,
            Price: productPrice.PriceCompany,
            BasePrice: productPrice.PriceCompany,
            Quantity: serviceProductQty
          });
        }

        return { ...prevState, QuoteItems };
      });
    },
    [commercailProducts, productPriceList]
  );

  const handleDeleteServer = (serverId: string) => {
    // Remove the server from QuoteServers
    setQuoteServers(prevState =>
      prevState ? prevState.filter(s => s.Id !== serverId) : prevState
    );

    // Remove the QuoteItems related with the server
    setQuoteAddEdit(prevState => {
      if (!prevState.QuoteItems) return prevState;
      // Find the QuoteItem that contains the server id in its Details
      const serverQuoteItem = prevState.QuoteItems.find(item => {
        if (item.Details) {
          try {
            const details = JSON.parse(item.Details);
            return details.Id === serverId;
          } catch (e) {
            return false;
          }
        }
        return false;
      });

      const mainQuoteItemId = serverQuoteItem
        ? serverQuoteItem.QuoteItemId
        : '';

      const filteredQuoteItems = prevState.QuoteItems.filter(item => {
        // Remove the main QuoteItem that represents the server
        if (item.Details) {
          try {
            const details = JSON.parse(item.Details);
            if (details.Id === serverId) return false;
          } catch (e) {
            console.error('Error parsing details:', e);
          }
        }
        // Also remove any QuoteItem whose QuoteItemUpperId equals to the main QuoteItem id
        if (
          item.QuoteItemUpperId &&
          item.QuoteItemUpperId === mainQuoteItemId
        ) {
          return false;
        }
        return true;
      });

      return { ...prevState, QuoteItems: filteredQuoteItems };
    });
  };

  const getData = useCallback(async () => {
    try {
      const userResult = await userService.getByGroupRuleId(
        'D0FCD45D-12F7-4588-B5F8-FB63A5A0A1F0'
      );
      setUsers(userResult);
    } catch (error) {
      console.log(`error in AddEditUser - function getData, error:${error}`);
    }
    try {
      const cProductResult = await commercialProductService.getAll();
      setCommercailProducts(cProductResult);
    } catch (error) {
      console.log(
        `error in CommercialProduct - function getData, error:${error}`
      );
    }
    try {
      const { ProductPrices } =
        await productPriceService.findProductPriceByCompanyId(1, 1);
      setProductPriceList(ProductPrices);
    } catch (error) {
      console.log(
        `error in CommercialProduct - function getData, error:${error}`
      );
    }
    try {
      const salesChannelResult = await salesChannelService.getAll();
      setSalesChannels(salesChannelResult);
    } catch (error) {
      console.log(`error in SalesChannel - function getData, error:${error}`);
    }
    if (quoteId) {
      try {
        const quoteResult = await QuoteService.get(quoteId);
        setQuoteAddEdit(quoteResult);

        // Para cada QuoteItem que possui Details preenchido: desserializa e adiciona em quoteServers
        if (quoteResult.QuoteItems) {
          const serversData = quoteResult.QuoteItems.filter(
            item => item.Details
          )
            .map(item => {
              try {
                return JSON.parse(item.Details || '{}');
              } catch (error) {
                console.error('Error parsing details:', error);
                return null;
              }
            })
            .filter(item => item !== null);
          setQuoteServers(serversData);
        }
      } catch (error) {
        console.log(`error in Quote - function getData, error:${error}`);
      }
    }
  }, [
    userService,
    commercialProductService,
    productPriceService,
    salesChannelService,
    QuoteService,
    quoteId
  ]);

  const renderQuoteItems = (
    commercialProductType: number,
    allowQuantityEditing: boolean = true
  ) =>
    quoteAddEdit.QuoteItems?.map(item => {
      const product = commercailProducts?.find(
        p =>
          p.CommercialProductType === commercialProductType &&
          p.CommercialProductId === item.CommercialProductId
      );
      if (!product) return null;

      const price = item.Price || 0;
      const total = price * item.Quantity;
      const discountPerc =
        (item.BasePrice ?? 0) > 0
          ? (((item.BasePrice ?? 0) - price) / (item.BasePrice ?? 0)) * 100
          : 0;
      const salesChannel = salesChannels?.find(
        sc => sc.SalesChannelId === Number(quoteAddEdit.SalesChannelId || 0)
      );
      const allowedDiscount = salesChannel?.MaxDiscount ?? 100;

      return (
        <tr
          key={item.CommercialProductId}
          style={
            discountPerc > allowedDiscount
              ? { backgroundColor: 'MistyRose' }
              : {}
          }
        >
          <td>{product?.Name || 'Produto não encontrado'}</td>
          <td>
            {allowQuantityEditing ? (
              <InputGroup>
                <FormControl
                  key={item.QuoteItemId}
                  type="number"
                  inputMode="numeric"
                  min="1"
                  step="1"
                  defaultValue={item.Quantity || 0}
                  onBlur={e => {
                    const newQuantity = parseInt(e.target.value, 10) || 0;
                    setQuoteAddEdit(prevState => {
                      const updatedItems = prevState.QuoteItems?.map(
                        quoteItem =>
                          quoteItem.QuoteItemId === item.QuoteItemId
                            ? { ...quoteItem, Quantity: newQuantity }
                            : quoteItem
                      );
                      return { ...prevState, QuoteItems: updatedItems };
                    });
                  }}
                />
              </InputGroup>
            ) : (
              <span>{item.Quantity}</span>
            )}
          </td>
          <td>R${(item.BasePrice || 0).toFixed(2)}</td>
          <td>
            <InputGroup>
              <InputGroup.Text>R$</InputGroup.Text>
              <FormControl
                key={item.QuoteItemId}
                type="number"
                inputMode="numeric"
                min="0"
                step=".01"
                defaultValue={price}
                onBlur={e => {
                  const newPrice = parseFloat(e.target.value) || 0;
                  setQuoteAddEdit(prevState => {
                    const updatedItems = prevState.QuoteItems?.map(quoteItem =>
                      quoteItem.QuoteItemId === item.QuoteItemId
                        ? { ...quoteItem, Price: newPrice }
                        : quoteItem
                    );
                    return { ...prevState, QuoteItems: updatedItems };
                  });
                }}
              />
            </InputGroup>
          </td>
          <td>{total.toFixed(2)}</td>
          <td>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() =>
                setQuoteAddEdit(prevState => {
                  const updatedItems = prevState.QuoteItems?.filter(
                    itemToRemove =>
                      itemToRemove.CommercialProductId !==
                      item.CommercialProductId
                  );
                  return { ...prevState, QuoteItems: updatedItems };
                })
              }
            >
              <Delete title="Excluir" size={22} />
            </Button>
          </td>
        </tr>
      );
    });

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <Container>
      <Title backButton>
        {' '}
        {quoteAddEdit?.QuoteId ? 'Editar' : 'Criar'} Cotação{' '}
      </Title>
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Accordion>
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              <AccordionHeaderContainer>
                <AccordionHeaderLeft>Projeto</AccordionHeaderLeft>
                <AccordionHeaderRight>
                  R${' '}
                  {calculateTotalByProductTypes([
                    0, 1, 2, 3, 4, 5, 6, 7
                  ]).toFixed(2)}
                </AccordionHeaderRight>
              </AccordionHeaderContainer>
            </Accordion.Header>
            <Accordion.Body>
              <Row className="mb-3">
                <Form.Group as={Col} sm={12} md={6}>
                  <Form.Label>Projeto</Form.Label>
                  <Form.Control
                    type="text"
                    name="projetcName"
                    placeholder="Projeto"
                    defaultValue={quoteAddEdit.ProjectName}
                    onBlur={e =>
                      setQuoteAddEdit(prevState => ({
                        ...prevState,
                        ProjectName: e.target.value
                      }))
                    }
                  />
                </Form.Group>
                <Form.Group as={Col} sm={12} md={6}>
                  <Form.Label>Vendedor</Form.Label>
                  <Form.Select
                    name="projectVendor"
                    required
                    value={quoteAddEdit.VendorAdminUserId}
                    onChange={e =>
                      setQuoteAddEdit(prevState => ({
                        ...prevState,
                        VendorAdminUserId: String(e.target.value)
                      }))
                    }
                  >
                    <option value={0}>Selecione um Vendedor</option>
                    {users &&
                      users.map(user => (
                        <option key={user.Id} value={user.Id}>
                          {user.Name}
                        </option>
                      ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    Campo Obrigatório
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group as={Col} sm={12} md={6}>
                  <Form.Label>Canal De Vendas</Form.Label>
                  <Form.Select
                    name="projectSalesChannel"
                    required
                    value={quoteAddEdit.SalesChannelId}
                    onChange={e =>
                      setQuoteAddEdit(prevState => ({
                        ...prevState,
                        SalesChannelId: Number(e.target.value)
                      }))
                    }
                  >
                    <option value={0}>Selecione um Canal de Venda</option>
                    {salesChannels &&
                      salesChannels.map(salesChannel => (
                        <option
                          key={salesChannel.SalesChannelId}
                          value={salesChannel.SalesChannelId}
                        >
                          {salesChannel.Name}
                        </option>
                      ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    Campo Obrigatório
                  </Form.Control.Feedback>
                </Form.Group>
                {/* novo combo box de tabela de preço  */}
                <Form.Group as={Col} sm={12} md={6}>
                  <Form.Label>Tabela de Preço</Form.Label>
                  <Form.Select
                    name="priceTable"
                    value={quoteAddEdit.PricelistType}
                    onChange={handlePriceTableChange}
                  >
                    <option value={1} selected>
                      Varejo
                    </option>
                    <option value={2}>Revenda</option>
                  </Form.Select>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group as={Col} sm={12} md={12}>
                  <Form.Label>Cliente</Form.Label>
                  <FilterText
                    callBackApi={async filterText => {
                      const clientsData =
                        await clientService.findClientByCnpjOrName(filterText);
                      setClients(clientsData);

                      if (clientsData && clientsData.length > 0) {
                        const clientSelectDiv =
                          document.getElementById('clientSelect');
                        if (clientSelectDiv) {
                          clientSelectDiv.style.display = 'block';
                        }
                      }

                      return clientsData || []; // Ensure it returns an array
                    }}
                    setDataToFilter={setClients}
                    placeholderInput="Razão Social, Nome Fantasia ou CNPJ"
                  />
                  <div
                    style={{ maxHeight: '200px', overflowY: 'auto' }}
                    id="clientSelect"
                  >
                    <Table className="table-striped text-center" responsive>
                      <tbody>
                        {clients &&
                          clients.map((client: IClient, index: number) => (
                            <tr
                              className="pointer"
                              key={client.Id.toString()}
                              onClick={() => {
                                setQuoteAddEdit(prevState => ({
                                  ...prevState,
                                  ClientId: client.Id,
                                  ClientName: client.Name,
                                  ClientFantasyName: client.FantasyName,
                                  ClientDocument: client.Cnpj
                                }));
                                const clientSelectDiv =
                                  document.getElementById('clientSelect');
                                if (clientSelectDiv) {
                                  clientSelectDiv.style.display = 'none';
                                }
                              }}
                            >
                              <td>
                                <RightArrow
                                  className="right-arrow"
                                  size="20"
                                  color="var(--medium-grey)"
                                />
                              </td>
                              <td>{client.FantasyName}</td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </div>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group as={Col} sm={12} md={6}>
                  <Form.Label>Nome do Cliente</Form.Label>
                  <Form.Control
                    type="text"
                    name="clientName"
                    placeholder="Nome do Cliente"
                    defaultValue={quoteAddEdit.ClientFantasyName}
                    onBlur={e =>
                      setQuoteAddEdit(prevState => ({
                        ...prevState,
                        ClientFantasyName: e.target.value
                      }))
                    }
                  />
                </Form.Group>
                <Form.Group as={Col} sm={12} md={6}>
                  <Form.Label>CNPJ</Form.Label>
                  <Form.Control
                    type="text"
                    name="clientDocument"
                    placeholder="CNPJ"
                    defaultValue={quoteAddEdit.ClientDocument}
                    onBlur={e =>
                      setQuoteAddEdit(prevState => ({
                        ...prevState,
                        ClientDocument: e.target.value
                      }))
                    }
                  />
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group as={Col} sm={12} md={6}>
                  <Form.Label>Razão Social</Form.Label>
                  <Form.Control
                    type="text"
                    name="clientLegalName"
                    placeholder="Razão Social"
                    defaultValue={quoteAddEdit.ClientName}
                    onChange={e =>
                      setQuoteAddEdit(prevState => ({
                        ...prevState,
                        ClientName: e.target.value
                      }))
                    }
                  />
                </Form.Group>
                <Form.Group as={Col} sm={12} md={6}>
                  <Form.Label>Desconto</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>R$</InputGroup.Text>
                    <FormControl
                      type="number"
                      name="discount"
                      placeholder="Desconto"
                      defaultValue={quoteAddEdit.Discount ?? 0}
                      onBlur={e =>
                        setQuoteAddEdit(prevState => ({
                          ...prevState,
                          Discount: Number(e.target.value)
                        }))
                      }
                    />
                  </InputGroup>
                </Form.Group>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="1">
            <Accordion.Header>
              <AccordionHeaderContainer>
                <AccordionHeaderLeft>Servidores</AccordionHeaderLeft>
                <AccordionHeaderRight>
                  R$ {calculateTotalByProductTypes([1]).toFixed(2)}
                </AccordionHeaderRight>
              </AccordionHeaderContainer>
            </Accordion.Header>
            <Accordion.Body>
              <Row className="mb-6">
                <Form.Group as={Col} sm={12} md={3}>
                  <Form.Label>Nome</Form.Label>
                  <Form.Control
                    type="text"
                    name="serverName"
                    placeholder="Nome"
                    defaultValue={server.Name}
                    value={server.Name}
                    onChange={e =>
                      setServer(prevState => ({
                        ...prevState,
                        Name: e.target.value
                      }))
                    }
                  />
                </Form.Group>
                <Form.Group as={Col} sm={12} md={3}>
                  <Form.Label>VCPU</Form.Label>
                  <Form.Control
                    type="number"
                    name="serverVcpu"
                    placeholder="VCPU"
                    value={server.Vcpu}
                    defaultValue={server.Vcpu}
                    isInvalid={serverValidation.Vcpu}
                    onChange={e =>
                      setServer(prevState => ({
                        ...prevState,
                        Vcpu: Number(e.target.value)
                      }))
                    }
                  />
                  {serverValidation.Vcpu && (
                    <Form.Control.Feedback type="invalid">
                      VCPU deve ser maior que 0.
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
                <Form.Group as={Col} sm={12} md={3}>
                  <Form.Label>Memória</Form.Label>
                  <Form.Control
                    type="number"
                    name="serverMemory"
                    placeholder="Memória"
                    value={server.Memory}
                    defaultValue={server.Memory}
                    isInvalid={serverValidation.Memory}
                    onChange={e =>
                      setServer(prevState => ({
                        ...prevState,
                        Memory: Number(e.target.value)
                      }))
                    }
                  />
                  {serverValidation.Memory && (
                    <Form.Control.Feedback type="invalid">
                      Memória deve ser maior que 0.
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
                <Form.Group as={Col} sm={12} md={3}>
                  <Form.Label>S.O.</Form.Label>
                  <Form.Select
                    name="serverSO"
                    required
                    value={server.SO}
                    isInvalid={serverValidation.SO}
                    onChange={e =>
                      setServer(prevState => ({
                        ...prevState,
                        SO: Number(e.target.value)
                      }))
                    }
                  >
                    <option value={0}>-</option>
                    <option value={1}>Windows Server</option>
                    <option value={2}>SLES SAP</option>
                    <option value={3}>RedHat Linux</option>
                    <option value={4}>Oracle Linux</option>
                    <option value={5}>Debian Linux</option>
                    <option value={6}>Cisco OS</option>
                    <option value={7}>Forti OS</option>
                    <option value={8}>CentOS</option>
                    <option value={9}>Suse</option>
                    <option value={10}>Ubuntu Server</option>
                    <option value={11}>Other (64-bit)</option>
                    <option value={12}>Fedora</option>
                    <option value={13}>Linux</option>
                  </Form.Select>
                  {serverValidation.SO && (
                    <Form.Control.Feedback type="invalid">
                      S.O. deve ser selecionado.
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Row>
              <Row className="mb-6">
                <Form.Group as={Col} sm={12} md={3}>
                  <Form.Label>Disco S.O.</Form.Label>
                  <Form.Control
                    type="number"
                    name="serverDiskSO"
                    placeholder="Disco S.O."
                    value={server.DiskSO}
                    defaultValue={server.DiskSO}
                    isInvalid={serverValidation.DiskSO}
                    onChange={e =>
                      setServer(prevState => ({
                        ...prevState,
                        DiskSO: Number(e.target.value)
                      }))
                    }
                  />
                  {serverValidation.DiskSO && (
                    <Form.Control.Feedback type="invalid">
                      Disco S.O. deve ser maior que 0.
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
                <Form.Group as={Col} sm={12} md={3}>
                  <Form.Label>Disco Dados</Form.Label>
                  <Form.Control
                    type="number"
                    name="serverDiskData"
                    placeholder="Disco Dados"
                    defaultValue={server.DiskData}
                    value={server.DiskData}
                    onChange={e =>
                      setServer(prevState => ({
                        ...prevState,
                        DiskData: Number(e.target.value)
                      }))
                    }
                  />
                </Form.Group>
                <Form.Group as={Col} sm={12} md={3}>
                  <Form.Label>Aplicação</Form.Label>
                  <Form.Select
                    name="serverApplication"
                    required
                    value={server.Application}
                    onChange={e =>
                      setServer(prevState => ({
                        ...prevState,
                        Application: Number(e.target.value)
                      }))
                    }
                  >
                    <option value={0}>-</option>
                    <option value={1}>SQL Standard</option>
                    <option value={2}>SQL Enterprise</option>
                    <option value={3}>Sybase</option>
                    <option value={5}>SQL Lite</option>
                    <option value={6}>MySQL</option>
                    <option value={7}>PostgreSQL</option>
                    <option value={8}>Apache + PHP</option>
                    <option value={9}>IIS</option>
                    <option value={10}>NGINX</option>
                    <option value={11}>Cassandra</option>
                    <option value={12}>Elasticsearch</option>
                    <option value={13}>SAP HANA</option>
                    <option value={14}>DB2</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group as={Col} sm={12} md={3}>
                  <Form.Label>Backup</Form.Label>
                  <Form.Select
                    name="serverBackup"
                    required
                    value={server.Backup}
                    onChange={e =>
                      setServer(prevState => ({
                        ...prevState,
                        Backup: Number(e.target.value)
                      }))
                    }
                  >
                    <option value={0}>-</option>
                    {/* <option value={1}>FÍSICO-CommV</option> */}
                    <option value={2}>VM-CommV</option>
                    <option value={3}>Guardian</option>
                  </Form.Select>
                </Form.Group>
              </Row>
              <Row className="mb-6">
                <Form.Group as={Col} sm={12} md={6}>
                  <Form.Label>Serviços</Form.Label>
                  {['switch'].map(type => (
                    <div key={`inline-${type}`} className="mb-3">
                      {/* <Form.Check
                        type="switch"
                        id="custom-switch"
                        label="EDR"
                        inline
                        checked={server.edr}
                        onChange={e =>
                          setServer(prevState => ({
                            ...prevState,
                            edr: Boolean(e.target.checked)
                          }))
                        }
                      /> */}
                      <Form.Check
                        type="switch"
                        id="custom-switch"
                        label="DRaaS"
                        inline
                        checked={server.Draas}
                        onChange={e =>
                          setServer(prevState => ({
                            ...prevState,
                            Draas: Boolean(e.target.checked)
                          }))
                        }
                      />
                      {/* <Form.Check
                        type="switch"
                        id="custom-switch"
                        label="Database Backup"
                        inline
                        checked={server.DbBackup}
                        onChange={e =>
                          setServer(prevState => ({
                            ...prevState,
                            DbBackup: Boolean(e.target.checked)
                          }))
                        }
                      /> */}
                    </div>
                  ))}
                </Form.Group>

                <Form.Group as={Col} sm={12} md={3}>
                  <Form.Label>Gerenciamento</Form.Label>
                  <Form.Select
                    name="serverManagement"
                    required
                    value={server.Management}
                    onChange={e =>
                      setServer(prevState => ({
                        ...prevState,
                        Management: Number(e.target.value)
                      }))
                    }
                  >
                    <option value={0}>-</option>
                    <option value={1}>STANDARD</option>
                    <option value={2}>ENTERPRISE</option>
                    <option value={3}>BASIS</option>
                    <option value={4}>BANCO DE DADOS</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group
                  as={Col}
                  sm={12}
                  md={3}
                  style={{ display: server.Backup > 0 ? 'block' : 'none' }}
                >
                  <Form.Label>Tamanho DB</Form.Label>
                  <Form.Control
                    type="number"
                    name="serverDiskDbBackup"
                    placeholder="Quantidade"
                    defaultValue={server.DiskDbBackup}
                    value={server.DiskDbBackup}
                    onChange={e =>
                      setServer(prevState => ({
                        ...prevState,
                        DiskDbBackup: Number(e.target.value)
                      }))
                    }
                  />
                </Form.Group>
              </Row>
              <Row className="mb-6">
                <Form.Group className="text-end">
                  <Button
                    variant="outline-primary"
                    title="Incluir Servidor"
                    onClick={e => {
                      addServerProduct(e);
                    }}
                  >
                    <Plus size="20" /> Incluir
                  </Button>
                </Form.Group>
              </Row>
              <Row className="mb-6 mt-2">
                {
                  //
                  <Table className="table-striped text-center" responsive>
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>VCPU</th>
                        <th>Memória</th>
                        <th>Discos (GB)</th>
                        <th>S.O.</th>
                        <th>Valor</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quoteServers &&
                        quoteServers.map(serverItem => (
                          <tr key={serverItem.Id}>
                            <td>{serverItem.Name}</td>
                            <td>{serverItem.Vcpu}</td>
                            <td>{serverItem.Memory}</td>
                            <td>{serverItem.DiskSO + serverItem.DiskData}</td>
                            <td>
                              {enumServerOSLabel.get(
                                serverItem.SO as enumServerOS
                              ) || 'S.O. não encontrado'}
                            </td>
                            <td>
                              R${getTotalServer(serverItem.Id).toFixed(2)}
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => {
                                  setServer(serverItem);
                                  handleDeleteServer(serverItem.Id);
                                  setQuoteServers(prevState =>
                                    prevState
                                      ? prevState.filter(
                                          s => s.Id !== serverItem.Id
                                        )
                                      : null
                                  );
                                }}
                              >
                                <Edit title="Editar" size={22} />
                              </Button>{' '}
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() =>
                                  handleDeleteServer(serverItem.Id)
                                }
                              >
                                <Delete title="Excluir" size={22} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                }
              </Row>
              <Row className="mb-6">
                <Table className="table-striped text-center" responsive>
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Quantidade</th>
                      <th>Preço Base</th>
                      <th>Preço</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quoteAddEdit.QuoteItems &&
                      Object.values(
                        quoteAddEdit.QuoteItems.reduce((acc, item) => {
                          const key = item.CommercialProductId;
                          if (!acc[key]) {
                            acc[key] = { ...item };
                          } else {
                            acc[key].Quantity += item.Quantity;
                            acc[key].Price = Math.max(
                              acc[key].Price || 0,
                              item.Price || 0
                            );
                          }
                          return acc;
                        }, {} as Record<number, IQuoteItem>)
                      ).map(item => {
                        const product = commercailProducts?.find(
                          p =>
                            p.CommercialProductType === 1 &&
                            p.CommercialProductId !== 1 &&
                            p.CommercialProductId === item.CommercialProductId
                        );
                        if (!product) return null;
                        const price = item.Price || 0;
                        const total = price * item.Quantity;

                        return (
                          <tr key={item.CommercialProductId}>
                            <td>{product?.Name || 'Produto não encontrado'}</td>
                            <td>{Number(item.Quantity).toFixed(4) || 0}</td>
                            <td>R${(item.BasePrice || 0).toFixed(2)}</td>
                            <td>
                              <InputGroup>
                                <InputGroup.Text>R$</InputGroup.Text>
                                <FormControl
                                  key={item.CommercialProductId}
                                  type="number"
                                  inputMode="numeric"
                                  min="0"
                                  step=".01"
                                  defaultValue={item.Price?.toFixed(2)}
                                  onBlur={e => {
                                    const newPrice = parseFloat(e.target.value);
                                    setQuoteAddEdit(prevState => {
                                      const updatedItems =
                                        prevState.QuoteItems?.map(quoteItem =>
                                          quoteItem.CommercialProductId ===
                                          item.CommercialProductId
                                            ? { ...quoteItem, Price: newPrice }
                                            : quoteItem
                                        );
                                      return {
                                        ...prevState,
                                        QuoteItems: updatedItems
                                      };
                                    });
                                  }}
                                />
                              </InputGroup>
                            </td>
                            <td>{total.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </Table>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="2">
            <Accordion.Header>
              <AccordionHeaderContainer>
                <AccordionHeaderLeft>Rede</AccordionHeaderLeft>
                <AccordionHeaderRight>
                  R$ {calculateTotalByProductTypes([2]).toFixed(2)}
                </AccordionHeaderRight>
              </AccordionHeaderContainer>
            </Accordion.Header>
            <Accordion.Body>
              <Row className="mb-3">
                <Form.Group as={Col} sm={12} md={6}>
                  <Form.Label>Produtos</Form.Label>
                  <Form.Select name="networkProduct">
                    {commercailProducts &&
                      commercailProducts
                        .filter(product => product.CommercialProductType === 2)
                        .map(product => (
                          <option
                            key={product.CommercialProductId}
                            value={product.CommercialProductId}
                          >
                            {product.Name}
                          </option>
                        ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group as={Col} sm={12} md={6}>
                  <Form.Label>Quantidade</Form.Label>
                  <Form.Control
                    type="number"
                    name="networkProductQty"
                    placeholder="Quantidade"
                    defaultValue={1}
                  />
                </Form.Group>
              </Row>
              <Row className="mb-6">
                <Form.Group as={Col} sm={12} md={12} className="text-end">
                  <Button
                    type="button"
                    variant="outline-primary"
                    title="Incluir Servidor"
                    onClick={e => {
                      addNetworkProduct(e);
                    }}
                  >
                    <Plus size="20" /> Incluir
                  </Button>
                </Form.Group>
              </Row>
              <Row className="mb-6  mt-2">
                <Table className="table-striped text-center" responsive>
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Quantidade</th>
                      <th>Preço Base</th>
                      <th>Preço</th>
                      <th>Total</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>{renderQuoteItems(2, true)}</tbody>
                </Table>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
          {/* <Accordion.Item eventKey="3">
            <Accordion.Header>
              <AccordionHeaderContainer>
                <AccordionHeaderLeft>Security</AccordionHeaderLeft>
                <AccordionHeaderRight>
                  R$ {calculateTotalByProductTypes([6]).toFixed(2)}
                </AccordionHeaderRight>
              </AccordionHeaderContainer>
            </Accordion.Header>
            <Accordion.Body>
              <Row className="mb-6">
                <Form.Group as={Col} sm={12} md={6}>
                  <Form.Label>Produtos</Form.Label>
                  <Form.Select name="securityProduct">
                    {commercailProducts &&
                      commercailProducts
                        .filter(product => product.CommercialProductType === 6)
                        .map(product => (
                          <option
                            key={product.CommercialProductId}
                            value={product.CommercialProductId}
                          >
                            {product.Name}
                          </option>
                        ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group as={Col} sm={12} md={3}>
                  <Form.Label>Quantidade</Form.Label>
                  <Form.Control
                    type="number"
                    name="securityProductQty"
                    placeholder="Quantidade"
                    defaultValue={1}
                  />
                </Form.Group>
                <Form.Group as={Col} sm={12} md={3}>
                  <Button
                    type="button"
                    variant="primary"
                    title="Adicionar Produto de Rede"
                    onClick={e => {
                      addSecurityProduct(e);
                    }}
                  >
                    <Plus size="20" />
                  </Button>
                </Form.Group>
              </Row>
              <Row className="mb-6">
                <Table className="table-striped text-center" responsive>
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Quantidade</th>
                      <th>Preço</th>
                      <th>Total</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>{renderQuoteItems(6, true)}</tbody>
                </Table>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="4">
            <Accordion.Header>
              <AccordionHeaderContainer>
                <AccordionHeaderLeft>Serviços</AccordionHeaderLeft>
                <AccordionHeaderRight>
                  R$ {calculateTotalByProductTypes([2]).toFixed(2)}
                </AccordionHeaderRight>
              </AccordionHeaderContainer>
            </Accordion.Header>
            <Accordion.Body>
              <Row className="mb-6">
                <Form.Group as={Col} sm={12} md={6}>
                  <Form.Label>Produtos</Form.Label>
                  <Form.Select name="serviceProduct">
                    {commercailProducts &&
                      commercailProducts
                        .filter(product => product.CommercialProductType === 2)
                        .map(product => (
                          <option
                            key={product.CommercialProductId}
                            value={product.CommercialProductId}
                          >
                            {product.Name}
                          </option>
                        ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group as={Col} sm={12} md={3}>
                  <Form.Label>Quantidade</Form.Label>
                  <Form.Control
                    type="number"
                    name="serviceProductQty"
                    placeholder="Quantidade"
                    defaultValue={1}
                  />
                </Form.Group>
                <Form.Group as={Col} sm={12} md={3}>
                  <Button
                    type="button"
                    variant="primary"
                    title="Adicionar Produto de Rede"
                    onClick={e => {
                      addServiceProduct(e);
                    }}
                  >
                    <Plus size="20" />
                  </Button>
                </Form.Group>
              </Row>
              <Row className="mb-6">
                <Table className="table-striped text-center" responsive>
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Quantidade</th>
                      <th>Preço Base</th>
                      <th>Preço</th>
                      <th>Total</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>{renderQuoteItems(2, true)}</tbody>
                </Table>
              </Row>
            </Accordion.Body>
          </Accordion.Item> */}
        </Accordion>
        <hr />
        <Button variant="primary" type="submit">
          Salvar
        </Button>
      </Form>
    </Container>
  );
};

export default AddEditQuote;
